import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Clock, Car, MapPin, User, CheckCircle, AlertCircle } from 'lucide-react';
import FeedbackForm from '../components/FeedbackForm';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';
import Announcements from './Announcements'; // Import for fetching announcements

const PendingFeedback = () => {
  const { logout, user } = useAuth();
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [historyFeedback, setHistoryFeedback] = useState([]); // New state for history
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchPendingFeedback();
    fetchHistoryFeedback(); // Fetch persistent history on mount
  }, [currentPage]);

  const fetchPendingFeedback = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/feedback/pending', {}, logout);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch pending feedback');
      }
      setPendingFeedback(data.pendingFeedback || []);
    } catch (error) {
      console.error('Fetch pending feedback error:', error);
      toast.error(error.message || 'Failed to fetch pending feedback');
      setPendingFeedback([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch persistent feedback history
  const fetchHistoryFeedback = async () => {
    try {
      const response = await fetchWithAuth('/api/feedback/history', {}, logout);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch feedback history');
      }
      setHistoryFeedback(data.history || []);
    } catch (error) {
      console.error('Fetch feedback history error:', error);
      toast.error(error.message || 'Failed to fetch feedback history');
      setHistoryFeedback([]);
    }
  };

  const handleSubmitFeedback = (feedbackData) => {
    setShowFeedbackForm(false);
    setSelectedItem(null);
    toast.success('Feedback submitted successfully!');
    // Copy feedback to history and remove from pending
    setHistoryFeedback(prev => [
      {
        id: Date.now(), // Temporary unique ID
        rideDetails: selectedItem.ride || selectedItem.rideDetails || {},
        userFeedback: feedbackData.userFeedback,
        finalRating: feedbackData.manualRating || feedbackData.aiRating?.rating || 5,
        aiRating: feedbackData.aiRating,
        createdAt: new Date().toISOString(),
      },
      ...prev
    ]);
    setPendingFeedback(prev => prev.filter(item => item.id !== selectedItem.id));
  };

  // Add a handler for opening feedback form for any item
  const handleOpenFeedbackFormUnified = (item) => {
    setSelectedItem(item);
    setShowFeedbackForm(true);
  };

  const getProfileLevelColor = (level) => {
    switch (level) {
      case 'Premium': return 'bg-purple-100 text-purple-800';
      case 'Platinum': return 'bg-gray-100 text-gray-800';
      case 'Gold': return 'bg-yellow-100 text-yellow-800';
      case 'Silver': return 'bg-gray-100 text-gray-600';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const formatTimeAgo = (date) => {
    try {
      const now = new Date();
      const rideDate = new Date(date);
      
      // Check if date is valid
      if (isNaN(rideDate.getTime())) {
        return 'Recently';
      }
      
      const diffInHours = Math.floor((now - rideDate) / (1000 * 60 * 60));
      
      if (diffInHours < 24) {
        return `${diffInHours} hours ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} days ago`;
      }
    } catch (error) {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending feedback...</p>
        </div>
      </div>
    );
  }

  const createDummyData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first before creating test data');
        return;
      }
      const response = await fetchWithAuth('/api/feedback/create-dummy-data', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }, logout);
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Please login first');
          return;
        }
        throw new Error(data.error || 'Failed to create dummy data');
      }
      toast.success('Dummy data created successfully!');
      fetchPendingFeedback(); // Refresh the list
    } catch (error) {
      console.error('Create dummy data error:', error);
      toast.error(error.message || 'Failed to create dummy data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pending Feedback</h1>
        <p className="text-gray-600">
          Rate your ride companions to help build a trusted community
        </p>
      </div>
      {/* Unified Pending Feedback List */}
      {pendingFeedback.length > 0 ? (
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Rides Awaiting Your Feedback</h2>
          <div className="space-y-4">
            {pendingFeedback.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 text-lg mb-1">{item.ride.source} → {item.ride.destination}</div>
                    <div className="text-sm text-gray-600 mb-1">{item.ride.rideTime ? new Date(item.ride.rideTime).toLocaleString() : ''}</div>
                    <div className="text-xs text-gray-500">{item.type === 'announcement' ? 'Your Announcement' : 'Matched Ride'}</div>
                  </div>
                  <button
                    onClick={() => handleOpenFeedbackFormUnified(item)}
                    className="mt-4 md:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Write Feedback</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No pending feedback</h3>
          <p className="text-gray-600 mb-4">You have given feedback for all your rides.</p>
        </div>
      )}
      {/* History Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
        {historyFeedback.length > 0 ? (
          <div className="space-y-4">
            {historyFeedback.map((item) => (
              <div key={item.id} className="bg-gray-100 rounded-lg shadow overflow-hidden">
                <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 text-lg mb-1">{item.ride?.source || item.rideDetails?.source || ''} → {item.ride?.destination || item.rideDetails?.destination || ''}</div>
                    <div className="text-sm text-gray-600 mb-1">{item.ride?.rideTime ? new Date(item.ride.rideTime).toLocaleString() : item.rideDetails?.rideTime ? new Date(item.rideDetails.rideTime).toLocaleString() : ''}</div>
                    <div className="text-xs text-gray-500">{item.type === 'announcement' ? 'Your Announcement' : 'Matched Ride'}</div>
                  </div>
                  <div className="mt-4 md:mt-0 text-green-600 flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Feedback Submitted</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No feedback history yet.</h3>
            <p className="text-gray-600 mb-4">Your submitted feedback will appear here.</p>
          </div>
        )}
      </div>
      {/* Feedback Form Modal for matches and announcements (unified) */}
      {showFeedbackForm && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <FeedbackForm
              matchId={selectedItem.type === 'match' ? selectedItem.id : `announcement-${selectedItem.id}`}
              otherUser={selectedItem.otherUser}
              rideDetails={selectedItem.ride}
              onSubmit={handleSubmitFeedback}
              onCancel={() => { setShowFeedbackForm(false); setSelectedItem(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingFeedback;